import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import {
  FaCalendarAlt,
  FaChartBar,
  FaChevronDown,
  FaCreditCard,
  FaDownload,
  FaSignOutAlt,
  FaUserCircle,
} from "react-icons/fa";
import "../styles/clientdashboard.css";

const PAYMENT_LABELS = {
  pending: "Pending payment",
  paid: "Paid",
  failed: "Payment failed",
  refunded: "Refunded",
};

const STUDY_LABELS = {
  draft: "Draft",
  waiting_for_client: "Waiting for client",
  data_collection: "Data collection",
  analysis: "Under analysis",
  report_preparation: "Preparing report",
  delivered: "Delivered",
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    phone: "",
    job_type: "",
  });

  useEffect(() => {
    getDashboardData();
  }, []);

  const getDashboardData = async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) return;

    setUser(userData.user);

    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userData.user.id)
      .single();

    setProfile(profileData);
    setForm({
      first_name: profileData?.first_name || "",
      middle_name: profileData?.middle_name || "",
      last_name: profileData?.last_name || "",
      phone: profileData?.phone || "",
      job_type: profileData?.job_type || "",
    });

    const { data: studiesData } = await supabase
      .from("studies")
      .select("*")
      .eq("user_id", userData.user.id)
      .order("created_at", { ascending: false });

    const normalizedStudies = studiesData || [];
    const orderIds = normalizedStudies.map((study) => study.order_id).filter(Boolean);
    let ordersById = {};

    if (orderIds.length) {
      const { data: ordersData } = await supabase
        .from("orders")
        .select("id, request_ref, service")
        .in("id", orderIds);

      ordersById = (ordersData || []).reduce((acc, order) => {
        acc[order.id] = order;
        return acc;
      }, {});
    }

    setStudies(
      normalizedStudies.map((study) => ({
        ...study,
        order_reference: ordersById[study.order_id]?.request_ref || study.order_id,
        service: ordersById[study.order_id]?.service,
      }))
    );
  };

  const toggleStudy = (study) => {
    setSelectedStudy(selectedStudy?.id === study.id ? null : study);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleUpdate = async () => {
    const { error } = await supabase.from("profiles").update(form).eq("id", user.id);

    if (!error) {
      setProfile((prev) => ({ ...prev, ...form }));
      setEditing(false);
    }
  };

  const openSurvey = (study) => {
    const params = new URLSearchParams();
    params.set("study_id", study.id);
    if (study.order_reference) params.set("request_ref", study.order_reference);
    const surveyPath = study.service === "feasibility1" ? "/technical-survey" : "/Marketsurvey";
    navigate(`${surveyPath}?${params.toString()}`);
  };

  const formatPaymentStatus = (value) => PAYMENT_LABELS[value] || value || "Pending payment";
  const formatStudyStatus = (value) => STUDY_LABELS[value] || value || "Draft";

  return (
    <div className="dashboard">
      <div className="profile-box">
        <div className="profile-header">
          <FaUserCircle className="big-avatar" />

          <div>
            <h3>
              {profile?.first_name} {profile?.last_name}
            </h3>
            <p>{user?.email}</p>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt />
            Logout
          </button>
        </div>

        {!editing ? (
          <div className="profile-info">
            <div className="info-grid">
              <div>
                <span>First name</span>
                <p>{profile?.first_name || "-"}</p>
              </div>

              <div>
                <span>Middle name</span>
                <p>{profile?.middle_name || "-"}</p>
              </div>

              <div>
                <span>Last name</span>
                <p>{profile?.last_name || "-"}</p>
              </div>

              <div>
                <span>Phone</span>
                <p>{profile?.phone || "-"}</p>
              </div>
            </div>

            <button onClick={() => setEditing(true)} className="edit-btn">
              Edit profile
            </button>
          </div>
        ) : (
          <div className="edit-form">
            <input name="first_name" value={form.first_name} onChange={handleChange} placeholder="First name" />
            <input name="middle_name" value={form.middle_name} onChange={handleChange} placeholder="Middle name" />
            <input name="last_name" value={form.last_name} onChange={handleChange} placeholder="Last name" />
            <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" />

            <div className="actions">
              <button onClick={handleUpdate} className="save">Save</button>
              <button onClick={() => setEditing(false)} className="cancel">Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-container">
        <div className="dashboard-header">
          <h2>
            <FaChartBar /> Client Dashboard
          </h2>
        </div>

        {studies.length === 0 ? (
          <div className="empty-box">
            <p>No studies yet.</p>
          </div>
        ) : (
          studies.map((study) => {
            const progress = Number.isFinite(study.survey_progress) ? study.survey_progress : 0;
            const lastUpdated = study.last_updated_at || study.created_at;

            return (
              <div key={study.id} className="card">
                <div className="card-header" onClick={() => toggleStudy(study)}>
                  <div>
                    <h3>
  {study.service === "feasibility1"
    ? "Technical & Operational Feasibility Study"
    : study.service === "feasibility2"
    ? "Market Feasibility Study"
    : study.title || "New Study"}
</h3>
                    <span className={`status ${study.status === "delivered" ? "done" : "progress"}`}>
                      {formatStudyStatus(study.status)}
                    </span>
                  </div>

                  <FaChevronDown className={`arrow ${selectedStudy?.id === study.id ? "open" : ""}`} />
                </div>

                {selectedStudy?.id === study.id ? (
                  <div className="card-details">
                    <p>
                      <FaCalendarAlt /> Created: {study.created_at ? new Date(study.created_at).toLocaleDateString() : "-"}
                    </p>

                    <p>
                      <FaCalendarAlt /> Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "-"}
                    </p>

                    <p className={study.payment_status === "paid" ? "paid" : "unpaid"}>
                      <FaCreditCard /> {formatPaymentStatus(study.payment_status)}
                    </p>

                    <p>Order ID: {study.order_reference || "-"}</p>
                    <p>Survey step: {study.survey_current_step || 1}</p>
                    <p>Survey progress: {progress}%</p>

                    {study.last_update_note ? <p>Latest note: {study.last_update_note}</p> : null}

                    <button className="edit-btn" onClick={() => openSurvey(study)}>
                      Continue Survey
                    </button>

                    {study.file_url ? (
                      <a href={study.file_url} target="_blank" rel="noreferrer">
                        <FaDownload /> Download Study
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
